import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { listAdminArchitects, adminExportUrl } from "../lib/adminPortal";
import { Download, Search, ExternalLink } from "lucide-react";
import StatusPill from "../components/StatusPill";
import { buildFilterBar } from "../lib/statusFilters";
import ListTable, { ListRow } from "../components/ListTable";

const FILTERS = buildFilterBar([
  "verified_active",
  "profile_published",
  "profile_unpublished",
  "deactivated",
]).map(({ key, label }) => ({ v: key, label }));

export default function AdminArchitectsDirectory() {
  const { user } = useRole();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const t = setTimeout(() => {
      listAdminArchitects(user.id, { status, q }).then((d) => alive && setData(d));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [user?.id, status, q]);

  return (
    <section className="animate-fade-in-up" data-testid="admin-architects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · Architect Directory</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Verified Architects.
          </h1>
        </div>
        <a
          href={adminExportUrl("architects", user?.id, { status, q })}
          className="wn-btn wn-btn-tertiary"
          data-testid="admin-architects-export"
        >
          <Download size={14} /> Export (CSV)
        </a>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-architects-filters">
          {FILTERS.map((f) => {
            const active = status === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setStatus(f.v)}
                data-testid={`admin-architects-filter-${f.v}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                  background: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#272636",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {f.label}
                {data?.counts?.[f.v] != null && (
                  <span className="tabular-nums" style={{ marginLeft: 6, opacity: 0.7 }}>
                    {data.counts[f.v]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-auto" style={{ minWidth: 240 }}>
          <Search size={14} style={{ color: "#6C6B85" }} />
          <input
            className="wn-input"
            placeholder="Name, firm, license"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-architects-search"
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <ListTable
        testId="admin-architects"
        columns={[
          { label: "Architect" },
          { label: "Service areas" },
          { label: "Profile" },
          { label: "Account" },
          { label: "Actions", align: "right" },
        ]}
        gridTemplate={ARCH_GRID}
        gap={ARCH_GAP}
        items={data?.items}
        emptyTitle="No architects match this filter."
        loadingHeight={62}
        renderRow={(a, _i, isLast) => (
          <ArchitectRow key={a.id} a={a} isLast={isLast} />
        )}
      />
    </section>
  );
}

/* Shared column template */
const ARCH_GRID = "minmax(0, 2fr) minmax(0, 1.4fr) 150px 130px 130px";
const ARCH_GAP = 18;

function ArchitectRow({ a, isLast }) {
  const navigate = useNavigate();
  const isDeact = a.account_status === "deactivated";
  return (
    <ListRow
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/admin/architects/${a.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/admin/architects/${a.id}`);
        }
      }}
      gridTemplate={ARCH_GRID}
      gap={ARCH_GAP}
      isLast={isLast}
      style={{ cursor: "pointer" }}
      data-testid={`admin-architect-row-${a.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 15,
              color: "#1B1F4B",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {a.name}
          </span>
          {a.verified_badge && (
            <span
              aria-label="Verified"
              title="Verified"
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "#4F6149",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          )}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {a.firm || "—"}
          {a.license_no && (
            <span
              className="tabular-nums"
              style={{
                fontStyle: "normal",
                color: "#3A5563",
                fontSize: 11,
                fontWeight: 600,
                marginLeft: 8,
              }}
            >
              · {a.license_no}
            </span>
          )}
        </div>
      </div>
      <div className="min-w-0 flex flex-wrap" style={{ gap: 4 }}>
        {(a.service_areas || []).slice(0, 4).map((s) => (
          <span
            key={s}
            style={{
              padding: "1px 7px",
              borderRadius: 999,
              border: "1px solid #D9D8E4",
              color: "#272636",
              fontSize: 10,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {s}
          </span>
        ))}
        {(a.service_areas || []).length > 4 && (
          <span
            className="tabular-nums"
            style={{ color: "#6C6B85", fontSize: 10, alignSelf: "center" }}
          >
            +{a.service_areas.length - 4}
          </span>
        )}
      </div>
      <div style={{ justifySelf: "start" }}>
        <StatusPill
          value={a.profile_published ? "profile_published" : "profile_unpublished"}
          variant="soft"
          testId={`admin-architect-publish-${a.id}`}
        />
      </div>
      <div style={{ justifySelf: "start" }}>
        <StatusPill
          value={isDeact ? "deactivated" : "active"}
          variant="soft"
          testId={`admin-architect-account-${a.id}`}
        />
      </div>
      <Link
        to={`/architects/${a.id}`}
        className="wn-btn wn-btn-tertiary inline-flex items-center"
        style={{
          paddingLeft: 0,
          paddingRight: 0,
          fontSize: 11,
          gap: 4,
          justifySelf: "end",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        View public <ExternalLink size={11} />
      </Link>
    </ListRow>
  );
}
